// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {useFormSubmit} from '@app/features/app/hooks/useFormSubmit';
import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import {
	MessageHistoryThresholdAccordion,
	MessageHistoryThresholdField,
	type MessageHistoryThresholdFormValues,
} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/sections/MessageHistoryThresholdContent';
import Guilds from '@app/features/guild/state/Guilds';
import Permission from '@app/features/permissions/state/Permission';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Form} from '@app/features/ui/components/form/Form';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import {extractTimestamp} from '@app/vendor/snowflake/SnowflakeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useMemo} from 'react';
import {useForm} from 'react-hook-form';

interface MessageHistoryThresholdModalProps {
	guildId: string;
}
export const MessageHistoryThresholdModal: React.FC<MessageHistoryThresholdModalProps> = observer(({guildId}) => {
	const {t} = useLingui();
	const guild = Guilds.getGuild(guildId);
	const canManageGuild = Permission.can(Permissions.MANAGE_GUILD, {guildId});
	const form = useForm<MessageHistoryThresholdFormValues>({
		defaultValues: {message_history_cutoff: guild?.messageHistoryCutoff ?? null},
	});
	const {handleSubmit, isSubmitting} = useFormSubmit({
		form,
		onSubmit: async (data) => {
			if (!guild) return;
			await GuildCommands.update(guild.id, {message_history_cutoff: data.message_history_cutoff});
			form.reset(data);
			ToastCommands.createToast({type: 'success', children: t`Message history threshold updated`});
			ModalCommands.pop();
		},
		defaultErrorField: 'message_history_cutoff',
	});
	useEffect(() => {
		if (!guild) return;
		if (form.formState.isDirty) return;
		form.reset({message_history_cutoff: guild.messageHistoryCutoff ?? null});
	}, [form, guild]);
	const guildCreatedAt = useMemo(() => {
		const timestamp = extractTimestamp(guildId);
		return new Date(timestamp);
	}, [guildId]);
	const maxDate = useMemo(() => new Date(), []);
	if (!guild) return null;
	return (
		<Modal.Root size="medium" centered onClose={ModalCommands.pop}>
			<Modal.Header title={t`Message History Threshold`} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description>
						<MessageHistoryThresholdAccordion />
					</Modal.Description>
					<Form form={form} onSubmit={handleSubmit}>
						<MessageHistoryThresholdField
							form={form}
							name="message_history_cutoff"
							canManageGuild={canManageGuild}
							guildCreatedAt={guildCreatedAt}
							maxDate={maxDate}
						/>
					</Form>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={ModalCommands.pop} disabled={isSubmitting}>
					<Trans>Cancel</Trans>
				</Button>
				<Button onClick={handleSubmit} disabled={!canManageGuild} submitting={isSubmitting}>
					<Trans>Save</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
